import React, { useMemo, useState } from 'react';
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
  Line,
  ZoomableGroup,
} from 'react-simple-maps';
import { useLocale } from '../../contexts/LocaleContext';
import { colors } from '../../config/colors';
import {
  LANDING_COUNTRY_SENTIMENTS,
  buildLandingWorldMapMarkers,
  buildLandingWorldMapRoutes,
  getSentimentColorForTone,
  getCountryFillForSentiment,
  getMarkerColorForRisk,
} from './landingDemoData';

const GEO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

export default function LandingWorldRiskMap({ className = '' }) {
  const { t } = useLocale();
  const [hoveredCountry, setHoveredCountry] = useState(null);
  const [hoveredMarker, setHoveredMarker] = useState(null);

  const markers = useMemo(() => buildLandingWorldMapMarkers(t), [t]);
  const routes = useMemo(() => buildLandingWorldMapRoutes(), []);

  const getSentimentForCountry = (countryName) =>
    LANDING_COUNTRY_SENTIMENTS.find((s) => s.Country === countryName);

  return (
    <div
      className={`relative h-full min-h-0 overflow-hidden rounded-md ${className}`}
      style={{ backgroundColor: colors.map.ocean }}
    >
      <ComposableMap
        projection="geoMercator"
        projectionConfig={{ scale: 140, center: [0, 20] }}
        style={{ width: '100%', height: '100%', backgroundColor: colors.map.ocean }}
      >
        <ZoomableGroup>
          <Geographies geography={GEO_URL}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const countryName = geo.properties.name;
                const sentiment = getSentimentForCountry(countryName);

                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    onMouseEnter={() => setHoveredCountry(countryName)}
                    onMouseLeave={() => setHoveredCountry(null)}
                    style={{
                      default: {
                        fill: getCountryFillForSentiment(countryName),
                        stroke: colors.map.stroke,
                        strokeWidth: 0.5,
                        outline: 'none',
                      },
                      hover: {
                        fill: sentiment
                          ? getSentimentColorForTone(sentiment.Tone)
                          : colors.map.landHover,
                        stroke: colors.border.medium,
                        strokeWidth: 1.5,
                        outline: 'none',
                        cursor: 'pointer',
                      },
                      pressed: {
                        fill: sentiment
                          ? getSentimentColorForTone(sentiment.Tone)
                          : colors.map.landHover,
                        stroke: colors.border.medium,
                        strokeWidth: 1.5,
                        outline: 'none',
                      },
                    }}
                  />
                );
              })
            }
          </Geographies>

          {routes.map((route) => (
            <Line
              key={route.id}
              from={route.from}
              to={route.to}
              stroke={route.isActive ? colors.brand[400] : colors.gray[300]}
              strokeWidth={2.5}
              strokeLinecap="round"
            />
          ))}

          {markers.map((marker, idx) => {
            const isHovered = hoveredMarker === idx;
            const riskColor = getMarkerColorForRisk(marker.riskScore);
            const radius = isHovered ? 9 : 7;

            return (
              <Marker key={marker.id} coordinates={marker.coordinates}>
                <circle
                  r={radius}
                  fill={riskColor}
                  stroke={colors.text.primary}
                  strokeWidth={2.5}
                  style={{ cursor: 'default', transition: 'all 0.2s' }}
                  onMouseEnter={() => setHoveredMarker(idx)}
                  onMouseLeave={() => setHoveredMarker(null)}
                />
              </Marker>
            );
          })}
        </ZoomableGroup>
      </ComposableMap>

      {hoveredCountry ? (
        <div className="pointer-events-none absolute right-3 top-3 max-w-xs rounded-md border border-border-light bg-background-card p-3 shadow-elevation-3">
          <p className="text-sm font-semibold text-text-primary">{hoveredCountry}</p>
          {(() => {
            const sentiment = getSentimentForCountry(hoveredCountry);
            if (!sentiment) {
              return (
                <p className="mt-1 text-xs text-text-secondary">{t('landingWorldMapNoSentiment')}</p>
              );
            }
            return (
              <p className="mt-1 text-xs text-text-secondary">
                {t('worldMapSentimentLabel')}{' '}
                <span
                  className="font-bold"
                  style={{ color: getSentimentColorForTone(sentiment.Tone) }}
                >
                  {sentiment.Tone > 0 ? '+' : ''}
                  {sentiment.Tone.toFixed(1)}
                </span>
              </p>
            );
          })()}
        </div>
      ) : null}

      {hoveredMarker != null && markers[hoveredMarker] ? (
        <div className="pointer-events-none absolute bottom-3 left-3 rounded-md border border-border-light bg-background-card p-3 shadow-elevation-3">
          <p className="text-sm font-semibold text-text-primary">{markers[hoveredMarker].label}</p>
          <p className="mt-1 text-xs text-text-secondary">
            {t('worldMapRiskLabel')}{' '}
            <span
              className="font-bold"
              style={{ color: getMarkerColorForRisk(markers[hoveredMarker].riskScore) }}
            >
              {t('worldMapRiskScore', { score: markers[hoveredMarker].riskScore })}
            </span>
          </p>
        </div>
      ) : null}
    </div>
  );
}
